import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment } from '@libraries/shared-env';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Announcement, Ok, Property } from '../models';

@Injectable()
export class ProjectsService {
  constructor(private http: HttpClient, private env: Environment) {}

  private get managerBaseUrl(): string {
    return this.env.managerClientConfig?.baseUrl ?? this.env.geolocationPhotoUploadUrl ?? '';
  }

  private defaultHeaders = {
    'content-type': 'application/json',
    accept: 'application/json',
    'bypass-Cognito': 'true',
  };

  private handleError = () =>
    catchError((e: HttpErrorResponse) => {
      return throwError(e);
    });

  public list(): any {
    // Configuration
    const url = this.managerBaseUrl + '/property_owners/properties';
    const options = {
      headers: this.defaultHeaders,
    };
    // Http Call
    return this.http.get<{ properties: Property[] }>(url, options).pipe(
      map((r: { properties: Property[] }) => r.properties),
      this.handleError()
    );
  }

  public createProject(payload: any) {
    const address = payload.projects.newProjectAddress;
    const questions = { ...payload.projects.newProjectInputs };
    const email = payload.cognito.user.email;
    questions.improvementList = questions.improvementList.filter(
      (eachImprovement: string) => eachImprovement !== 'Other'
    );
    const configuredPayload = {
      user: {
        email,
      },
      metadata: {
        improvements: questions.improvementList,
        how_soon: questions.urgency,
      },
      property_search: {
        address: address.line_1,
        line_2: '',
        city: address.city,
        state: address.state,
        zipcode: address.zipcode,
        apn: address.apn,
        fips: address.fips,
      },
    };
    const options = {
      headers: {
        'accept-version': 'v1',
      },
    };
    return this.http.post(
      `${this.managerBaseUrl}/api/property_searches/public`,
      configuredPayload,
      options
    );
  }

  public updateUser(payload: any) {
    const url = `${this.managerBaseUrl}/property_owners`;
    const options = {
      headers: this.defaultHeaders,
    };
    return this.http.put<Ok>(url, payload, options).pipe(this.handleError());
  }

  public getUser() {
    const url = `${this.managerBaseUrl}/property_owners`;
    const options = {
      headers: this.defaultHeaders,
    };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  public getContractorMatch(token: string) {
    const url = `${this.managerBaseUrl}/property_owners/contractors/${token}`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
        'cache-control': 'no-cache',
        Accept: 'application/json',
      },
    };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  public getOwnerDocuments(token: string) {
    const url = `${this.managerBaseUrl}/property_owners/documents?project_token=${token}`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
        'cache-control': 'no-cache',
        Accept: 'application/json',
      },
    };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  // This is changed from userID to Owner Token
  public getUserSecureToken(
    projectId: string,
    ownerToken?: string,
    businessOwnerId?: number,
    requestedSecureDocumentTitlesEnabled = false
  ) {
    let url = `${this.managerBaseUrl}/property_owners/upload_links/${projectId}`;
    const params: string[] = [];
    if (ownerToken) {
      params.push('owner_nti_token=' + ownerToken);
    } else if (businessOwnerId) {
      params.push('business_owner_id=' + businessOwnerId);
    }
    if (requestedSecureDocumentTitlesEnabled) {
      params.push('requested_secure_document_titles_enabled=true');
    }
    if (params.length) {
      url = url + '?' + params.join('&');
    }
    const options = {
      headers: { ...this.defaultHeaders, 'skip-Cognito': 'true' },
    };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  public checkYodleeAccountCreated(projectId: string, userId: string) {
    const url = `${this.managerBaseUrl}/property_owners/upload_links/${projectId}/yodlee_account_created?owner_id=${userId}`;
    const options = {
      headers: { ...this.defaultHeaders, 'skip-Cognito': 'true' },
    };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  public getOwnersForProject(projectId: string) {
    const url = `${this.managerBaseUrl}/property_owners/project/owners.json/?project_token=${projectId}`;
    const options = { headers: this.defaultHeaders };
    return this.http.get<Ok>(url, options).pipe(this.handleError());
  }

  public propertySearch(payload: any) {
    const url = `${this.managerBaseUrl}/api/property_searches/public`;
    const options = { headers: { 'accept-version': 'v1' } };
    return this.http.post(url, payload, options);
  }

  public checkEmail(email: string, ignoreAccountExists: boolean) {
    const url = `${this.managerBaseUrl}/property_owners/validate_email`;
    const options = {
      headers: this.defaultHeaders,
      params: {
        email,
        ...(ignoreAccountExists && {
          ignore_email_exists: `${ignoreAccountExists}`,
        }),
      },
    };
    return this.http.get<Ok>(url, options);
  }

  public registerEmail(email: string) {
    const url = `${this.managerBaseUrl}/api/prequalification/email_register`;
    const options = { headers: this.defaultHeaders, params: { email } };
    return this.http.post<Ok>(url, options).pipe(this.handleError());
  }

  public createIneligibleProperty(payload: any) {
    const url = `${this.managerBaseUrl}/ineligible_properties`;
    const options = { headers: this.defaultHeaders };
    return this.http.post(url, { ineligible_property: payload }, options);
  }

  getAnnouncement() {
    return this.http.get<Announcement>(
      `${this.managerBaseUrl}/property_owners/announcement.json`
    );
  }
}
